import React, { useState, useRef, useEffect, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { AttachmentView } from "@/components/AttachmentView";
import { MessageReactions } from "@/components/MessageReactions";
import { toast } from "sonner";
import { Send, Trash2, AlertTriangle, MessageSquare, Filter, Paperclip, X, Loader2 } from "lucide-react";

const ROLE_LABEL = { founder: "Founder", admin: "Head", employee: "Operator" };
const initials = (n = "") => n.split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
const timeOf = (ts) => { try { return new Date(ts).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };
const dayOf = (ts) => { try { return new Date(ts).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }); } catch { return ""; } };
const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

export const ChatPanel = ({ compact = false }) => {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [text, setText] = useState("");
  const [tag, setTag] = useState("update");
  const [filter, setFilter] = useState("all");
  const [pendingFile, setPendingFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionQuery, setMentionQuery] = useState("");
  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  const fileRef = useRef(null);

  const { data: messages = [] } = useQuery({
    queryKey: ["chat-messages"],
    queryFn: async () => (await api.get("/chat/messages")).data,
    refetchInterval: 4000,
  });
  const { data: members = [] } = useQuery({
    queryKey: ["chat-members"],
    queryFn: async () => (await api.get("/chat/members")).data,
    staleTime: 60000,
  });

  const markRead = async () => { await api.post("/chat/read"); qc.invalidateQueries({ queryKey: ["chat-unread"] }); };
  useEffect(() => { markRead(); }, [messages.length]); // eslint-disable-line
  useEffect(() => { if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: "smooth" }); }, [messages.length, filter]);

  const mentionRegex = useMemo(() => {
    const names = members.map((m) => m.name).filter(Boolean).sort((a, b) => b.length - a.length).map(escapeRegExp);
    return names.length ? new RegExp(`@(${names.join("|")})`, "g") : null;
  }, [members]);

  const computeMentions = (t) => members.filter((m) => m.name && t.includes(`@${m.name}`)).map((m) => m.user_id);

  const send = useMutation({
    mutationFn: () => api.post("/chat/messages", {
      text: text.trim(), tag,
      mentions: computeMentions(text),
      attachment: pendingFile || undefined,
    }),
    onSuccess: () => {
      setText(""); setTag("update"); setPendingFile(null); setMentionOpen(false);
      qc.invalidateQueries({ queryKey: ["chat-messages"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not send message."),
  });
  const remove = useMutation({
    mutationFn: (id) => api.delete(`/chat/messages/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["chat-messages"] }),
  });

  const onChangeText = (e) => {
    const v = e.target.value;
    setText(v);
    const m = v.match(/@([\p{L}\p{N} ._-]*)$/u);
    if (m) { setMentionOpen(true); setMentionQuery(m[1].toLowerCase()); }
    else setMentionOpen(false);
  };

  const pickMention = (mem) => {
    setText((prev) => prev.replace(/@([\p{L}\p{N} ._-]*)$/u, `@${mem.name} `));
    setMentionOpen(false);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const filteredMembers = members
    .filter((m) => m.user_id !== user?.user_id && (m.name || "").toLowerCase().includes(mentionQuery))
    .slice(0, 6);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) { toast.error("File too large (max 10 MB)."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const res = await api.post("/chat/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPendingFile(res.data);
    } catch (err) {
      toast.error("Upload failed. Please try again.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const submit = (e) => {
    e?.preventDefault();
    if ((text.trim() || pendingFile) && !send.isPending && !uploading) send.mutate();
  };

  const renderText = (t) => {
    if (!t || !mentionRegex) return t;
    const parts = [];
    let last = 0; let match; const re = new RegExp(mentionRegex.source, "g");
    while ((match = re.exec(t)) !== null) {
      if (match.index > last) parts.push(t.slice(last, match.index));
      parts.push(<span key={match.index} className="rounded bg-gold/20 px-1 font-semibold text-gold-dark">@{match[1]}</span>);
      last = match.index + match[0].length;
    }
    if (last < t.length) parts.push(t.slice(last));
    return parts;
  };

  const shown = messages.filter((m) => filter === "all" || m.tag === filter);
  let lastDay = null;

  return (
    <div className="flex flex-col" style={{ height: compact ? 380 : "calc(100vh - 210px)" }} data-testid="chat-panel">
      <div className="flex items-center gap-1 border-b border-slate-100 px-3 py-2">
        <Filter className="h-3.5 w-3.5 text-slate-400" />
        {["all", "update", "issue"].map((f) => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`chat-filter-${f}`}
            className={cn("rounded-full px-3 py-1 text-xs font-medium capitalize transition-colors", filter === f ? "bg-navy text-white" : "text-slate-500 hover:bg-slate-100")}>
            {f === "all" ? "All" : f === "issue" ? "Issues" : "Updates"}
          </button>
        ))}
      </div>

      <div className="flex-1 space-y-3 overflow-y-auto bg-slate-50/60 px-3 py-4" data-testid="chat-messages">
        {shown.length === 0 && (
          <div className="flex h-full flex-col items-center justify-center text-center text-sm text-slate-400">
            <MessageSquare className="mb-2 h-8 w-8" /> No messages yet. Start the conversation!
          </div>
        )}
        {shown.map((m) => {
          const mine = m.user_id === user?.user_id;
          const showDay = m.created_at.slice(0, 10) !== lastDay;
          lastDay = m.created_at.slice(0, 10);
          return (
            <div key={m.message_id}>
              {showDay && (
                <div className="my-2 flex justify-center">
                  <span className="rounded-full bg-white px-3 py-0.5 text-[11px] text-slate-400 shadow-sm">{dayOf(m.created_at)}</span>
                </div>
              )}
              <div className={cn("flex items-end gap-2", mine ? "flex-row-reverse" : "")} data-testid={`chat-msg-${m.message_id}`}>
                {!mine && <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-navy text-[10px] font-bold text-gold">{initials(m.name)}</div>}
                <div className={cn("group max-w-[78%] rounded-2xl px-3 py-2 shadow-sm", mine ? "bg-navy text-white" : "bg-white text-navy")}>
                  {!mine && (
                    <div className="mb-0.5 flex items-center gap-2">
                      <span className="text-xs font-semibold text-navy">{m.name}</span>
                      <span className="text-[10px] text-slate-400">{ROLE_LABEL[m.role] || m.role}</span>
                    </div>
                  )}
                  {m.tag === "issue" && <Badge className="mb-1 border-red-200 bg-red-100 text-red-700"><AlertTriangle className="mr-1 h-3 w-3" />Issue</Badge>}
                  {m.text && <p className={cn("whitespace-pre-wrap break-words text-sm", mine ? "text-white" : "text-slate-700")}>{renderText(m.text)}</p>}
                  {m.attachment && <AttachmentView attachment={m.attachment} />}
                  <MessageReactions message={m} invalidateKey={["chat-messages"]} dark={mine} />
                  <div className={cn("mt-1 flex items-center gap-2", mine ? "justify-end" : "")}>
                    <span className={cn("text-[10px]", mine ? "text-white/60" : "text-slate-400")}>{timeOf(m.created_at)}</span>
                    {(mine || ["founder", "admin"].includes(user?.role)) && (
                      <button onClick={() => remove.mutate(m.message_id)} data-testid={`chat-delete-${m.message_id}`}
                        className={cn("opacity-0 transition-opacity group-hover:opacity-100", mine ? "text-white/70 hover:text-white" : "text-slate-300 hover:text-red-500")} title="Delete">
                        <Trash2 className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      <form onSubmit={submit} className="relative border-t border-slate-100 bg-white p-3">
        {mentionOpen && filteredMembers.length > 0 && (
          <div className="absolute bottom-full left-3 z-20 mb-1 w-64 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xl" data-testid="mention-dropdown">
            {filteredMembers.map((mem) => (
              <button key={mem.user_id} type="button" onClick={() => pickMention(mem)} data-testid={`mention-${mem.user_id}`}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-slate-50">
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-navy text-[9px] font-bold text-gold">{initials(mem.name)}</span>
                <span className="font-medium text-navy">{mem.name}</span>
                <span className="ml-auto text-[10px] text-slate-400">{ROLE_LABEL[mem.role] || mem.role}</span>
              </button>
            ))}
          </div>
        )}

        <div className="mb-2 flex items-center gap-2">
          <span className="text-xs text-slate-400">Post as:</span>
          <button type="button" onClick={() => setTag("update")} data-testid="chat-tag-update"
            className={cn("rounded-full px-3 py-1 text-xs font-medium transition-colors", tag === "update" ? "bg-emerald-100 text-emerald-700" : "text-slate-400 hover:bg-slate-100")}>Work Update</button>
          <button type="button" onClick={() => setTag("issue")} data-testid="chat-tag-issue"
            className={cn("inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium transition-colors", tag === "issue" ? "bg-red-100 text-red-700" : "text-slate-400 hover:bg-slate-100")}>
            <AlertTriangle className="h-3 w-3" /> Issue
          </button>
        </div>

        {pendingFile && (
          <div className="mb-2 inline-flex items-center gap-2 rounded-lg bg-slate-100 px-3 py-1.5 text-xs text-navy" data-testid="pending-attachment">
            <Paperclip className="h-3.5 w-3.5" />
            <span className="max-w-[200px] truncate">{pendingFile.name}</span>
            <button type="button" onClick={() => setPendingFile(null)} data-testid="remove-attachment" className="text-slate-400 hover:text-red-500"><X className="h-3.5 w-3.5" /></button>
          </div>
        )}

        <div className="flex items-center gap-2">
          <input ref={fileRef} type="file" className="hidden" onChange={onFile} data-testid="chat-file-input"
            accept="image/*,.pdf,.csv,.txt,.xlsx,.docx" />
          <Button type="button" variant="outline" size="icon" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="chat-attach-btn" className="rounded-lg shrink-0">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
          </Button>
          <Input ref={inputRef} value={text} onChange={onChangeText} data-testid="chat-input" className="flex-1" maxLength={2000}
            placeholder={tag === "issue" ? "Describe the problem…  (type @ to mention)" : "Share a work update…  (type @ to mention)"} />
          <Button type="submit" disabled={(!text.trim() && !pendingFile) || send.isPending || uploading} data-testid="chat-send" className="rounded-lg bg-navy text-white hover:bg-navy-900 shrink-0">
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </form>
    </div>
  );
};
